import {
  createContext,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface OutlierHeatsState {
  heatIds: string[];
  materialName: string | null;
}

interface OutlierHeatsContextType {
  outlierHeats: OutlierHeatsState | null;
  openOutlierHeats: (heatIds: string[], materialName: string) => void;
  closeOutlierHeats: () => void;
  page: number;
  setPage: (page: number) => void;
}

const OutlierHeatsContext = createContext<OutlierHeatsContextType | null>(null);

export const useOutlierHeats = () => {
  const context = use(OutlierHeatsContext);
  if (!context) {
    throw new Error(
      "useOutlierHeats must be used within an OutlierHeatsProvider"
    );
  }
  return context;
};

export const OutlierHeatsProvider = ({ children }: { children: ReactNode }) => {
  const [outlierHeats, setOutlierHeats] = useState<OutlierHeatsState | null>(
    null
  );
  const [page, setPage] = useState(0);
  const historyPushedRef = useRef(false);

  const openOutlierHeats = useCallback(
    (heatIds: string[], materialName: string) => {
      setOutlierHeats({ heatIds, materialName });
      setPage(0);
      window.history.pushState({ outlierHeatsOpen: true }, "");
      historyPushedRef.current = true;
    },
    []
  );

  const closeOutlierHeats = useCallback(() => {
    setOutlierHeats(null);
    setPage(0);
    if (historyPushedRef.current) {
      historyPushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (outlierHeats !== null) {
        historyPushedRef.current = false;
        setOutlierHeats(null);
        setPage(0);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [outlierHeats]);

  const value = useMemo(
    () => ({
      outlierHeats,
      openOutlierHeats,
      closeOutlierHeats,
      page,
      setPage,
    }),
    [outlierHeats, openOutlierHeats, closeOutlierHeats, page]
  );

  return <OutlierHeatsContext value={value}>{children}</OutlierHeatsContext>;
};
